import { useRef, useState, ReactNode } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { PIShell, Drill, PIInsight, PracticeLadder } from "@/components/pi/PIShell";
import { PianoDiagram, KeyHighlight } from "@/components/PianoDiagram";
import { KeyPicker } from "@/components/KeyPicker";
import { KeyProvider, useKeyOrDefault } from "@/theory/key";
import { midiToPitchName, tonicMidiForPc } from "@/theory/melody";
import { kickAudio } from "@/audio/piano";
import { playRhythm, RhythmHandle } from "@/audio/rhythm";

/**
 * Piano Intermediate · Lesson 25 — Formation of All Seventh Chords.
 *
 * Jason builds every seventh chord without a scale, from two choices:
 *   1. the triad — five families: major, minor, diminished, augmented, sus4;
 *   2. the note added on top — a major 7th (octave − 1), a minor 7th
 *      (octave − 2) or a major 6th (fifth + 2; on a diminished triad that
 *      major 6th is called a diminished 7th).
 * Major, minor and diminished take all three; augmented and sus4 take the
 * two sevenths. Bonus from the end of the video: C7♭5.
 *
 * Bespoke visual: SeventhFamilyTree — one card per triad family, each chord
 * with its formula and a play button. Active chord is shown on the PianoDiagram.
 */

interface ChordType {
  name: string;
  symbol: string;
  formula: string;
  offsets: number[];
  description: string;
}

interface Family {
  name: string;
  color: string;
  borderClass: string;
  chords: ChordType[];
}

const FAMILIES: Family[] = [
  {
    name: "Major triad · 1 3 5",
    color: "#f59e0b",
    borderClass: "border-amber-400/40",
    chords: [
      { name: "Major 7th", symbol: "maj7", formula: "1 – 3 – 5 – 7", offsets: [0, 4, 7, 11], description: "Major chord + major 7th. Find the 7th by going up to the octave and down one chromatic step." },
      { name: "Dominant 7th", symbol: "7", formula: "1 – 3 – 5 – ♭7", offsets: [0, 4, 7, 10], description: "Major chord + minor 7th — the octave minus two semitones." },
      { name: "Major 6th", symbol: "6", formula: "1 – 3 – 5 – 6", offsets: [0, 4, 7, 9], description: "Major chord + major 6th (the fifth plus two). Jason calls it grand — royal and posh." },
    ],
  },
  {
    name: "Minor triad · 1 ♭3 5",
    color: "#06b6d4",
    borderClass: "border-cyan-400/40",
    chords: [
      { name: "Minor-major 7th", symbol: "m(maj7)", formula: "1 – ♭3 – 5 – 7", offsets: [0, 3, 7, 11], description: "Minor chord + major 7th. A funny name, but it makes sense." },
      { name: "Minor 7th", symbol: "m7", formula: "1 – ♭3 – 5 – ♭7", offsets: [0, 3, 7, 10], description: "Minor chord + minor 7th." },
      { name: "Minor 6th", symbol: "m6", formula: "1 – ♭3 – 5 – 6", offsets: [0, 3, 7, 9], description: "Slightly tricky name: a minor chord with a MAJOR 6th." },
    ],
  },
  {
    name: "Diminished triad · 1 ♭3 ♭5",
    color: "#8b5cf6",
    borderClass: "border-violet-400/40",
    chords: [
      { name: "Diminished major 7th", symbol: "°(maj7)", formula: "1 – ♭3 – ♭5 – 7", offsets: [0, 3, 6, 11], description: "Diminished chord + major 7th. Very rare, very exotic." },
      { name: "Half-diminished", symbol: "m7♭5", formula: "1 – ♭3 – ♭5 – ♭7", offsets: [0, 3, 6, 10], description: "Diminished chord + minor 7th: a minor 7th chord with a flat 5. One of Jason's favourites." },
      { name: "Diminished 7th", symbol: "°7", formula: "1 – ♭3 – ♭5 – 𝄫7", offsets: [0, 3, 6, 9], description: "Diminished chord + major 6th. That note is called a double-flat 7th, so the chord is a diminished 7th — a stack of minor thirds, and symmetric." },
    ],
  },
  {
    name: "Augmented triad · 1 3 ♯5",
    color: "#ec4899",
    borderClass: "border-pink-400/40",
    chords: [
      { name: "Augmented major 7th", symbol: "maj7♯5", formula: "1 – 3 – ♯5 – 7", offsets: [0, 4, 8, 11], description: "Augmented chord + major 7th — also called major 7th ♯5. A very interesting vibe." },
      { name: "Augmented 7th", symbol: "7♯5", formula: "1 – 3 – ♯5 – ♭7", offsets: [0, 4, 8, 10], description: "Augmented chord + minor 7th. We just say augmented 7th, or 7♯5." },
    ],
  },
  {
    name: "Suspended triad · 1 4 5",
    color: "#10b981",
    borderClass: "border-emerald-400/40",
    chords: [
      { name: "Major 7th sus4", symbol: "maj7sus4", formula: "1 – 4 – 5 – 7", offsets: [0, 5, 7, 11], description: "Major 7th with the 3rd suspended up to the 4th." },
      { name: "Dominant 7th sus4", symbol: "7sus4", formula: "1 – 4 – 5 – ♭7", offsets: [0, 5, 7, 10], description: "The 3 became 4. Add the 2nd and the five notes sound like an Indian janya raga." },
    ],
  },
];

export default function PianoIntermediate25() {
  const { lesson, course, accent } = findLesson("piano-intermediate", "25");
  return (
    <LessonShell
      course={course}
      courseSlug="piano-intermediate"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <KeyProvider defaultTonic="C" availableQualities={["major"]}>
        <PIShell
          title="Formation of All Seventh Chords"
          exerciseGoal="Five triad families, three notes to add on top. Build any seventh chord from scratch — no scale needed."
          bpm={80}
          difficulty="Intermediate"
        >
          <Body />
        </PIShell>
      </KeyProvider>
    </LessonShell>
  );
}

function Body() {
  const k = useKeyOrDefault();
  const tonicMidi = tonicMidiForPc(k.tonicPc);

  const [selectedFamilyIdx, setSelectedFamilyIdx] = useState<number | null>(null);
  const [selectedChordSymbol, setSelectedChordSymbol] = useState<string | null>(null);
  const handleRef = useRef<RhythmHandle | null>(null);

  // Find the currently selected chord type
  const activeChord: ChordType | null =
    selectedFamilyIdx !== null && selectedChordSymbol !== null
      ? FAMILIES[selectedFamilyIdx].chords.find((c) => c.symbol === selectedChordSymbol) ?? null
      : null;

  const playChord = async (offsets: number[]) => {
    kickAudio();
    handleRef.current?.stop();
    // Root in the left hand (Jason: "reinforce the sound ... by playing the
    // root in your left hand"), the chord above it.
    const notes = [tonicMidi - 12, ...offsets.map((off) => tonicMidi + off)].map((m) =>
      midiToPitchName(m, k.useFlats)
    );
    handleRef.current = await playRhythm(
      [{ beat: 0, notes, durationSteps: 4 }],
      { bpm: 80, subdivide: 1, totalSteps: 4, loop: false, click: false }
    );
    setTimeout(() => handleRef.current?.stop(), 3000);
  };

  const handleSelectChord = (familyIdx: number, chord: ChordType) => {
    setSelectedFamilyIdx(familyIdx);
    setSelectedChordSymbol(chord.symbol);
    playChord(chord.offsets);
  };

  // Piano highlights for the active chord
  const highlights: KeyHighlight[] = activeChord
    ? activeChord.offsets.map((off, i) => ({
        pc: ((k.tonicPc + off) % 12 + 12) % 12,
        tone: (i === 0 ? "root" : "voice") as "root" | "voice",
        label: degreeLabel(off - activeChord.offsets[0]),
      }))
    : [];

  return (
    <>
      <StudyDeck>
        <KeyPicker />
        {activeChord && (
          <PianoDiagram
            highlights={highlights}
            octaves={2}
            startOctave={3}
            playable
            ariaLabel={`${activeChord.name} chord tones`}
          />
        )}
        {!activeChord && (
          <p className="text-[12.5px] text-cyan-300/70">
            Click any chord in the family tree below to see and hear it on the keyboard.
          </p>
        )}
      </StudyDeck>

      <FadeIn>
        <Drill number="1" label="The big idea" title="A triad, plus one note">
          <p>
            Someone asks you for an A major 7th, a C minor 7th flat 5 or a G7sus4 — a scale won't
            always help you find it fast. So build it from two choices. First, the triad, from one
            of <strong>five families</strong>: major (1 3 5), minor (1 ♭3 5), diminished (1 ♭3 ♭5),
            augmented (1 3 ♯5) and suspended (1 4 5). Second, the note on top: a{" "}
            <strong>major 7th</strong> (go up to the octave, down one semitone), a{" "}
            <strong>minor 7th</strong> (the octave, down two) or a <strong>major 6th</strong> (the
            fifth, up two).
          </p>
          <PIQuote attribution="Jason · lesson video">
            "To form any kind of extended 7th chord you need the triads. Major, minor, diminished,
            augmented, suspended."
          </PIQuote>
          <p>
            Put a major 6th on a diminished triad and the chord is called a{" "}
            <strong>diminished 7th</strong>: the diminished 7th interval is a major 7th flattened
            twice, which lands on the same key as the major 6th.
          </p>
          <PIInsight>
            <p>
              Focus on intervals more than anything else. Remember a minor chord as root, minor
              third, perfect fifth — on its own, not as "a major chord with a flat third". And
              reinforce every chord by playing its root in the left hand; the player below does.
            </p>
          </PIInsight>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="2" label="The family tree" title="Click to hear each chord type">
          <SeventhFamilyTree
            families={FAMILIES}
            selectedFamilyIdx={selectedFamilyIdx}
            selectedChordSymbol={selectedChordSymbol}
            onSelectChord={handleSelectChord}
          />

          {/* Active chord display */}
          {activeChord && (
            <motion.div
              key={activeChord.symbol}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-3 rounded-xl border border-amber-400/30 bg-amber-500/8 p-3"
            >
              <div className="flex flex-wrap items-baseline gap-3">
                <span className="text-2xl font-bold text-amber-200">
                  {k.tonicName}{activeChord.symbol}
                </span>
                <span className="text-[13px] text-cyan-300/70">{activeChord.formula}</span>
              </div>
              <p className="mt-1 text-[12px] text-cyan-200/80">{activeChord.description}</p>
            </motion.div>
          )}
          <p className="mt-3 text-[13px] text-cyan-300/80">
            Play every chord in all its shapes, too — the added note can go on top, at the bottom
            or in the middle. You don't need the names of the inversions; you need to be able to
            play them.
          </p>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="3" label="Make your own" title="Beyond the textbook">
          <p>
            Once you have the textbook seventh chords, alter one note of your own. Jason's example:
            take the dominant 7th and flatten the 5th — {k.tonicName}7♭5, root, 3rd, ♭5 and ♭7.
          </p>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="4" label="Practice ladder" title="Fifteen minutes, five families">
          <PracticeLadder
            intro={
              <>
                One triad family per rung. Play the root in the left hand, the chord in the right,
                and say the name out loud as you play it.
              </>
            }
            rungs={[
              {
                title: "Major family (maj7 · 7 · 6)",
                minutes: 3,
                accent: "amber",
                detail: "The same major triad with a major 7th, a minor 7th, then a major 6th on top. Listen to the one note that changes.",
              },
              {
                title: "Minor family (m(maj7) · m7 · m6)",
                minutes: 3,
                accent: "cyan",
                detail: "The same three additions on the minor triad. Remember: m6 is a minor chord with a MAJOR 6th.",
              },
              {
                title: "Diminished family (°(maj7) · m7♭5 · °7)",
                minutes: 3,
                accent: "rose",
                detail: "The diminished 7th is a stack of minor thirds — C-E♭-F♯-A. Invert it and you get the same chord starting on E♭, F♯ or A.",
              },
              {
                title: "Augmented and sus4",
                minutes: 3,
                accent: "violet",
                detail: "maj7♯5 and 7♯5 on the augmented triad; maj7sus4 and 7sus4 on the sus4 triad.",
              },
              {
                title: "Three more keys",
                minutes: 3,
                accent: "emerald",
                detail: "Pick another root in the key picker and build all thirteen again from scratch.",
              },
            ]}
          />
        </Drill>
      </FadeIn>
    </>
  );
}

/* ── Bespoke visual: SeventhFamilyTree ──────────────────────────────── */
function SeventhFamilyTree({
  families,
  selectedFamilyIdx,
  selectedChordSymbol,
  onSelectChord,
}: {
  families: Family[];
  selectedFamilyIdx: number | null;
  selectedChordSymbol: string | null;
  onSelectChord: (familyIdx: number, chord: ChordType) => void;
}) {
  return (
    <div className="my-4 grid gap-3 sm:grid-cols-2">
      {families.map((family, fi) => {
        const isFamilyActive = fi === selectedFamilyIdx;
        return (
          <div
            key={family.name}
            className={`rounded-2xl border p-3 transition-all ${family.borderClass} ${
              isFamilyActive ? "bg-zinc-900/70" : "bg-zinc-900/30"
            }`}
            style={{
              boxShadow: isFamilyActive
                ? `0 0 0 1px ${family.color}33, 0 4px 20px ${family.color}11`
                : undefined,
            }}
          >
            {/* Family header */}
            <div
              className="mb-2 text-[10px] font-bold uppercase tracking-[0.22em]"
              style={{ color: family.color }}
            >
              {family.name}
            </div>

            {/* Two chord type buttons */}
            <div className="space-y-2">
              {family.chords.map((chord) => {
                const isSelected =
                  isFamilyActive && selectedChordSymbol === chord.symbol;
                return (
                  <motion.button
                    key={chord.symbol}
                    layout
                    onClick={() => onSelectChord(fi, chord)}
                    className={`w-full rounded-xl border p-2.5 text-left transition-all ${
                      isSelected
                        ? "border-amber-400/60 bg-amber-500/12 ring-1 ring-amber-400/40"
                        : `border-zinc-700/60 bg-zinc-800/40 hover:border-zinc-600 hover:bg-zinc-800/70`
                    }`}
                  >
                    <div className="flex items-baseline justify-between gap-2">
                      <span
                        className={`font-mono text-[15px] font-bold ${
                          isSelected ? "text-amber-200" : "text-cyan-100"
                        }`}
                      >
                        {chord.symbol}
                      </span>
                      <span className="text-[10px] font-mono text-cyan-400/60">
                        {chord.formula}
                      </span>
                    </div>
                    <div
                      className={`mt-0.5 text-[11px] ${
                        isSelected ? "text-amber-100/80" : "text-cyan-300/60"
                      }`}
                    >
                      {chord.name}
                    </div>
                    {isSelected && (
                      <motion.p
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        className="mt-1.5 text-[11px] leading-snug text-cyan-200/70"
                      >
                        {chord.description}
                      </motion.p>
                    )}
                  </motion.button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ── Shared helpers ─────────────────────────────────────────────────── */
function FadeIn({ children }: { children: ReactNode }) {
  const reduced = useReducedMotion();
  return (
    <motion.div
      initial={reduced ? false : { opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.55, ease: "easeOut" }}
    >
      {children}
    </motion.div>
  );
}

function StudyDeck({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="relative mb-4 rounded-2xl border border-cyan-400/30 bg-zinc-950/85 p-3">
      <button onClick={() => setOpen((o) => !o)} className="flex w-full items-center justify-between">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-amber-300">✦ Study deck</div>
          <div className="text-[12px] text-cyan-300/70">Key picker · master keyboard</div>
        </div>
        <span className="text-[12px] font-mono text-cyan-300">{open ? "▴" : "▾"}</span>
      </button>
      <motion.div
        initial={false}
        animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }}
        transition={{ duration: 0.25 }}
        style={{ overflow: "hidden" }}
      >
        <div className="mt-3 space-y-3">{children}</div>
      </motion.div>
    </div>
  );
}

function PIQuote({ children, attribution }: { children: ReactNode; attribution?: string }) {
  return (
    <figure className="my-4 rounded-2xl border-l-4 border-amber-400/80 bg-zinc-900/60 px-5 py-4">
      <blockquote
        className="text-[17px] leading-relaxed text-cyan-50/95"
        style={{ fontFamily: '"Cormorant Garamond", serif', fontStyle: "italic" }}
      >
        {children}
      </blockquote>
      {attribution && (
        <figcaption className="mt-2 text-[11px] uppercase tracking-[0.18em] text-amber-300/80">
          — {attribution}
        </figcaption>
      )}
    </figure>
  );
}

/**
 * Name a chord tone from its actual distance above the root, so a m7♭5 shows
 * "♭5" and "♭7" rather than borrowing the labels of a major 7th chord.
 */
function degreeLabel(semitonesAboveRoot: number): string {
  switch (((semitonesAboveRoot % 12) + 12) % 12) {
    case 0:  return "1";
    case 1:  return "♭2";
    case 2:  return "2";
    case 3:  return "♭3";
    case 4:  return "3";
    case 5:  return "4";
    case 6:  return "♭5";
    case 7:  return "5";
    case 8:  return "♭6";
    case 9:  return "6";
    case 10: return "♭7";
    default: return "7";
  }
}
